/* eslint-disable react/jsx-no-leaked-render */
/* eslint-disable no-mixed-spaces-and-tabs */
/* eslint-disable indent */
import React, { useCallback, useState } from 'react';
import { Avatar, Box, CloseButton, Flex, Text, VStack } from '@chakra-ui/react';
import { useLocation } from 'react-router-dom';
import { hasNavItemClaim, LinkItems, SUPER_USER_ID } from '../../../constants';
import { NavItem } from './NavItem';
import { Claim, CurrentUserType, ModulesEnum, Role } from '../../../types';
import { useVideoInstructionStore } from '../../../store';
import {
	DropdownMenu,
	DropdownMenuTrigger,
} from '../../../pages/Messenger/common-components/Menu/Menu';
import { UserMenu } from './UserMenu';
import { NavAccordion } from '../../NavAccordion/NavAccordion';
import { DealFinder, S8SmallLogo } from '../../../assets';
import { ProfileLoadingPlaceholder } from './ProfileLoadingPlaceholder';
import {
	hasCoachingAccess,
	hasCommunityAccess,
	useSignOut,
} from '../../../utils';
import { CruzButton } from './CruzButton';
import {
	getGuestNavigationModalTarget,
	GuestNavigationModal,
	GuestNavigationModalTarget,
} from './GuestNavigationModal';
import { MOBILE_NAV_HEIGHT } from '../../../constants/layout';

interface MenuContentProps {
	onClose: VoidFunction;
	isLoading: boolean;
	data?: CurrentUserType;
	amountUnreadAdminMessages: number;
	amountUnreadCommunity: number;
}
// TODO: refactoring

export const MenuContent = ({
	onClose,
	isLoading,
	data,
	amountUnreadCommunity,
	amountUnreadAdminMessages,
}: MenuContentProps) => {
	const fullName = `${data?.firstName} ${data?.lastName}`;
	const claimsIds = data?.claimsIds || [];
	const isAdmin = data?.roleId === Role.Admin;
	const isWebinarGuest = data?.roleId === Role.Guest;
	const modulesIds = data?.modulesIds || [];
	const hasExpertNetwork = modulesIds.includes(ModulesEnum.ExpertNetwork);
	const hasCruzAccess =
		claimsIds.includes(Claim.CruzControl) &&
		modulesIds.includes(ModulesEnum.DealTools);
	const [guestModalTarget, setGuestModalTarget] =
		useState<GuestNavigationModalTarget | null>(null);
	const isCommunityAvailable = data?.isCommunityAvailable;
	const isCommunityInstructionCompleted =
		data?.isCommunityInstructionCompleted;

	// const { amountUnreadAdminMessages, amountCommunityUnreadChats } =
	// 	useUnreadSidebarCounterStore(state => state);

	const { onOpen } = useVideoInstructionStore();

	const getFilteredNavLinks = useCallback(() => {
		return LinkItems.filter(elem => {
			if ('adminOnly' in elem && elem.adminOnly && !isAdmin) {
				return false;
			}
			if (elem.guestRestricted && data?.roleId === Role.Guest) {
				return false;
			}
			if (
				elem.id === Claim.Courses &&
				!claimsIds.includes(Claim.Courses)
			) {
				return false;
			}
			if (elem.id === Claim.Messenger) {
				return isWebinarGuest || hasCommunityAccess(claimsIds);
			}

			if (elem.id === Claim.Coaching) {
				return isWebinarGuest || hasCoachingAccess(claimsIds);
			}

			if (
				elem.id === Claim.Vendors &&
				!isWebinarGuest &&
				!claimsIds.includes(Claim.Vendors)
			) {
				return false;
			}

			if (elem.id === Claim.Vault && !claimsIds.includes(Claim.Vault)) {
				return false;
			}
			if (elem.id === Claim.TenantFinder && !isAdmin) {
				return false;
			}

			return true;
		});
	}, [claimsIds, isAdmin, isWebinarGuest]);

	const location = useLocation();
	const onSignOut = useSignOut();

	const accordionItems = getFilteredNavLinks().filter(item =>
		Boolean(item.accordion),
	);

	const isSuperAdmin = data?.userId === SUPER_USER_ID;

	return (
		<Flex
			direction="column"
			align="space-between"
			bg="blue.100"
			transition=".3s"
			w={{ base: 'full' }}
			overflowX="hidden"
			overflowY="auto"
			h="100dvh"
			pt="env(safe-area-inset-top)"
			pl="max(20px, env(safe-area-inset-left))"
			pr="max(20px, env(safe-area-inset-right))"
			pos="fixed">
			<Flex
				justifyContent="space-between"
				alignItems="center"
				minH={MOBILE_NAV_HEIGHT}>
				<Flex alignItems={'center'}>
					<S8SmallLogo />
				</Flex>
				<CloseButton
					color="white"
					size="lg"
					display={{ base: 'flex' }}
					onClick={onClose}
				/>
			</Flex>
			<VStack>
				{getFilteredNavLinks()
					.filter(item => item.text === 'Quick Start')
					.map(item => (
						<NavItem
							isActive={location.pathname === item.link}
							key={item.text}
							icon={item.icon}
							link={item.link}
							isDisabled={false}
							isInAccordion={false}
							menuExpanded={true}
							heightPadding={'16px'}
							optionalClassName={item.optionalClassName}>
							{item.text}
						</NavItem>
					))}
				{accordionItems.length ? (
					<NavAccordion
						className="guide-deal-finder"
						icon={<DealFinder />}
						title={'Deal Finder'}
						menuExpanded={true}>
						{accordionItems.map(item => {
							const target = getGuestNavigationModalTarget(
								item.id,
							);
							const hasItemAccess =
								hasNavItemClaim(item, claimsIds) &&
								(item.id !== Claim.Vendors || hasExpertNetwork);
							const isGuestLocked =
								isWebinarGuest &&
								target !== null &&
								!hasItemAccess;

							return (
								<NavItem
									onAction={
										isGuestLocked
											? () => setGuestModalTarget(target)
											: !hasNavItemClaim(item, claimsIds)
											? onOpen
											: undefined
									}
									isActive={location.pathname === item.link}
									key={item.text}
									icon={item.icon}
									link={item.link}
									isDisabled={!hasItemAccess}
									showDisabledStyle={!isGuestLocked}
									menuExpanded={true}
									heightPadding={'8px'}>
									{item.text}
								</NavItem>
							);
						})}
					</NavAccordion>
				) : null}
				{getFilteredNavLinks()
					.filter(
						item =>
							Boolean(!item.accordion) &&
							item.text !== 'Quick Start',
					)
					.map(item => {
						const isCoachingTab = item.id === Claim.Coaching;

						const isTenantFinder = item.id === Claim.TenantFinder;

						if (isSuperAdmin && isCoachingTab) {
							return null;
						}

						const isCommunity = item.text === 'Community';
						const target = getGuestNavigationModalTarget(item.id);
						const isAdminOnly =
							'adminOnly' in item && item.adminOnly;
						const hasItemAccess = isAdminOnly
							? isAdmin
							: isCommunity
							? hasCommunityAccess(claimsIds)
							: isCoachingTab
							? hasCoachingAccess(claimsIds)
							: claimsIds.includes(item.id);
						const hasGuestAccess =
							hasItemAccess &&
							hasExpertNetwork &&
							(!isCommunity || Boolean(isCommunityAvailable)) &&
							(!isCoachingTab ||
								Boolean(
									isCommunityAvailable &&
										isCommunityInstructionCompleted,
								));
						const isGuestLocked =
							isWebinarGuest &&
							target !== null &&
							!hasGuestAccess;
						const link = isCommunity
							? !isCommunityInstructionCompleted
								? '/instruction'
								: item.link
							: item.link;

						return (
							<NavItem
								onAction={
									isGuestLocked
										? () => setGuestModalTarget(target)
										: !hasItemAccess && item.id > 0
										? onOpen
										: undefined
								}
								isActive={location.pathname === link}
								showAvailableBadge={
									isCommunity &&
									isCommunityAvailable &&
									!isCommunityInstructionCompleted
								}
								key={item.text}
								icon={item.icon}
								avatar={item.avatar}
								link={link}
								isDisabled={
									isGuestLocked
										? true
										: isCommunity
										? !isCommunityAvailable
										: isCoachingTab
										? !isCommunityInstructionCompleted ||
										  !isCommunityAvailable
										: isTenantFinder
										? !hasItemAccess
										: Boolean(
												!hasItemAccess ||
													item.disabledBadgeText,
										  )
									// true
									// (permissionId === UserPermission.CourseOnly &&
									// 	item.link !== '/courses') ||
									// isLoading
								}
								showDisabledStyle={!isGuestLocked}
								disabledBadgeText={item.disabledBadgeText}
								menuExpanded={true}
								isInAccordion={item.accordion}
								unreadCount={
									isCoachingTab
										? amountUnreadAdminMessages
										: isCommunity
										? amountUnreadCommunity
										: undefined
								}
								heightPadding={'16px'}>
								{item.text}
							</NavItem>
						);
					})}
			</VStack>

			<Box
				mt={'auto'}
				pt="24px"
				mb="max(24px, env(safe-area-inset-bottom))">
				<VStack spacing={'4px'} align={'stretch'}>
					{isWebinarGuest || claimsIds.includes(Claim.CruzControl) ? (
						<CruzButton
							menuExpanded={true}
							hasAccess={hasCruzAccess}
						/>
					) : null}
				</VStack>
				<DropdownMenu>
					{isLoading ? (
						<ProfileLoadingPlaceholder
							wrapperProps={{
								maxW: '260px',
							}}
						/>
					) : (
						<Box>
							<DropdownMenuTrigger asChild={true}>
								<Flex
									cursor="pointer"
									align="center"
									justify="space-between"
									gap="15px">
									<Avatar name={fullName} boxSize="40px" />
									<Flex
										direction="column"
										minWidth="100px"
										flex={1}
										gap="2px">
										<Text color="white" fontSize="16px">
											{fullName}
										</Text>
										<Text
											color="gray.400"
											fontSize="12px"
											isTruncated={true}>
											{data?.email}
										</Text>
									</Flex>
								</Flex>
							</DropdownMenuTrigger>
						</Box>
					)}
					<UserMenu onSignOut={onSignOut} />
				</DropdownMenu>
			</Box>
			<GuestNavigationModal
				target={guestModalTarget}
				onClose={() => setGuestModalTarget(null)}
			/>
		</Flex>
	);
};
